import React from 'react';
import addDays from 'date-fns/addDays';
import format from 'date-fns/format';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import InsertLinkIcon from '@mui/icons-material/InsertLink';

import {
  LinkType,
  StatusConfig,
  SummaryGroup,
} from '../types/paymentLinks.types';

export const createInitialForm = () => ({
  amount: '',
  barcode: '',
  description: '',
  destinationType: 'Whatsapp',
  destinationValue: '',
  dispatch: false,
  dueDate: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
  externalReference: '',
  type: 'amount' as LinkType,
});

export const initialForm = createInitialForm();

export const initialFilters = {
  search: '',
  status: '',
  type: '',
};

export const STATUS_ORDER = [
  'created',
  'open',
  'identified',
  'checkout_started',
  'payment_submitted',
  'analyzing',
  'processing',
  'paid',
  'rejected',
  'expired',
  'invalid',
  'error',
];

export const statusConfig: Record<string, StatusConfig> = {
  created: { label: 'Criado', summaryLabel: 'Criado', color: 'default' },
  open: { label: 'Aberto', summaryLabel: 'Aberto', color: 'info' },
  identified: {
    label: 'Identificado',
    summaryLabel: 'Identif.',
    color: 'info',
  },
  checkout_started: {
    label: 'Pagamento iniciado',
    summaryLabel: 'Iniciado',
    color: 'primary',
  },
  payment_submitted: {
    label: 'Pagamento enviado',
    summaryLabel: 'Pagto env.',
    color: 'secondary',
  },
  analyzing: {
    label: 'Em análise',
    summaryLabel: 'Análise',
    color: 'warning',
  },
  processing: {
    label: 'Processando',
    summaryLabel: 'Proc.',
    color: 'warning',
  },
  paid: { label: 'Pago', summaryLabel: 'Pago', color: 'success' },
  rejected: { label: 'Rejeitado', summaryLabel: 'Rejeitado', color: 'error' },
  expired: { label: 'Expirado', summaryLabel: 'Expirado', color: 'default' },
  invalid: { label: 'Inválido', summaryLabel: 'Inválido', color: 'default' },
  error: { label: 'Erro', summaryLabel: 'Erro', color: 'error' },
};

export const statusDescriptions: Record<string, string> = {
  created: 'Link criado e disponível. O cliente ainda não abriu o link.',
  open: 'O cliente abriu o link, mas ainda não começou o pagamento.',
  identified: 'Cliente identificado. O pagamento ainda não foi iniciado.',
  checkout_started:
    'Pagamento iniciado. O cliente está preenchendo ou confirmando os dados.',
  payment_submitted: 'Dados de pagamento enviados. Aguardando a confirmação.',
  analyzing:
    'Pagamento em análise. Pode levar alguns instantes para confirmar.',
  processing:
    'Pagamento em processamento. Acompanhe o histórico para ver o ponto exato.',
  paid: 'Pagamento confirmado.',
  rejected: 'Pagamento recusado, cancelado ou não aprovado.',
  expired: 'Link vencido ou fora do prazo para pagamento.',
  invalid: 'Link ou cobrança inválida para pagamento.',
  error: 'Ocorreu uma falha ao consultar ou processar o link.',
};

export const eventLabels: Record<string, string> = {
  analyzing: 'Pagamento em análise',
  checkout_started: 'Pagamento iniciado',
  created: 'Link criado',
  dispatch_failed: 'Falha no envio',
  expired: 'Link expirado',
  identified: 'Pagador identificado',
  invalid: 'Link inválido',
  open: 'Link aberto',
  opened: 'Link aberto',
  paid: 'Pagamento confirmado',
  payment_submitted: 'Pagamento enviado',
  processing: 'Processamento',
  rejected: 'Pagamento rejeitado',
  sent: 'Link enviado',
  status_changed: 'Situação atualizada',
  status_checked: 'Situação conferida',
};

// Situações já encerradas: não faz sentido oferecer "atualizar situação".
export const FINAL_STATUSES = ['paid', 'rejected', 'expired', 'invalid'];
export const NOT_SENDABLE_STATUSES = ['paid', 'expired'];
export const OUTLINED_STATUSES = ['created', 'open', 'identified'];

// Fluxo resumido: caminho principal até o pagamento e os desfechos alternativos.
export const FLOW_MAIN = [
  'created',
  'open',
  'identified',
  'checkout_started',
  'payment_submitted',
  'analyzing',
  'processing',
  'paid',
];
export const FLOW_OUTCOMES = ['rejected', 'expired', 'invalid', 'error'];

export const typeLabel: Record<string, string> = {
  amount: 'Valor',
  boleto: 'Boleto',
};

// Agrupa os 12 status em poucos indicadores legíveis em qualquer largura.
export const SUMMARY_GROUPS: SummaryGroup[] = [
  {
    key: 'total',
    label: 'Total',
    statuses: null,
    icon: <InsertLinkIcon />,
    color: 'primary',
  },
  {
    key: 'waiting',
    label: 'Aguardando',
    statuses: ['created', 'open', 'identified'],
    icon: <HourglassTopIcon />,
    color: 'info',
  },
  {
    key: 'in_progress',
    label: 'Em andamento',
    statuses: [
      'checkout_started',
      'payment_submitted',
      'analyzing',
      'processing',
    ],
    icon: <AutorenewIcon />,
    color: 'warning',
  },
  {
    key: 'paid',
    label: 'Pagos',
    statuses: ['paid'],
    icon: <CheckCircleOutlineIcon />,
    color: 'success',
  },
  {
    key: 'failed',
    label: 'Não concluídos',
    statuses: ['rejected', 'expired', 'invalid', 'error'],
    icon: <ErrorOutlineIcon />,
    color: 'error',
  },
];
